// todo:ai-review
import { toValue } from "vue"
import type { BookInfo, ContentNode, RenderedContent, SQLiteExampleNode } from "#layers/books/types"

export interface BookPageData {
	bookInfo: BookInfo | undefined
	tree: ContentNode[]
	content: RenderedContent | null
	currentNode: ContentNode | SQLiteExampleNode | undefined
	children: ContentNode[]
}

/**
 * Find a node in the tree by its content path.
 */
function findNodeByPath(
	nodes: ContentNode[],
	bookId: string,
	targetPath: string,
): ContentNode | undefined {
	for (const node of nodes) {
		const nodePathSegment = node.path.replace(`/book/${bookId}/`, "")
		if (nodePathSegment === targetPath) {
			return node
		}
		if (node.children) {
			const found = findNodeByPath(node.children, bookId, targetPath)
			if (found) return found
		}
	}
	return undefined
}

/**
 * Consolidated data fetching for a book page.
 *
 * Optimized to avoid unnecessary refetches:
 * - Book info and tree are cached by bookId (stable across page navigation)
 * - Only content is refetched when contentPath changes
 *
 * IMPORTANT: Uses static string keys for useAsyncData to ensure proper
 * payload serialization during nuxt generate. The key is evaluated once
 * using toValue() to get the immediate value, ensuring the same cache key
 * is used during both SSR and client hydration.
 */
export function useBookPage(bookId: MaybeRef<string>, contentPath: MaybeRef<string>) {
	// Get immediate values for cache keys (must be stable across SSR/client)
	const bookIdValue = toValue(bookId)
	const contentPathValue = toValue(contentPath)

	// Create refs for reactive watching
	const bookIdRef = toRef(bookId)
	const contentPathRef = toRef(contentPath)

	// Fetch book metadata - stable cache, only changes when bookId changes
	const { data: books } = useAsyncData("books-list", () => $fetch<BookInfo[]>("/api/books"))

	// Fetch tree - stable cache per book, only changes when bookId changes
	const { data: tree } = useAsyncData(
		`book-tree-${bookIdValue}`,
		async () => {
			const id = bookIdRef.value
			if (!id) return []
			return $fetch<ContentNode[]>(`/api/books/${id}/tree`)
		},
		{ watch: [bookIdRef] },
	)

	// Computed values derived from cached data (no refetch needed)
	const bookInfo = computed(() => books.value?.find((b) => b.id === bookIdRef.value))

	const currentNode = computed(() => {
		const path = contentPathRef.value
		const id = bookIdRef.value
		if (!tree.value || !path || !id) return undefined
		return findNodeByPath(tree.value, id, path)
	})

	const children = computed(() => {
		const path = contentPathRef.value
		// Book root - show book's children (the tree)
		if (!path) {
			return tree.value || []
		}
		// Container node - show node's children
		if (currentNode.value?.type === "container") {
			return currentNode.value.children || []
		}
		// Unit node - no children
		return []
	})

	// Check if current node is an SQLite example (doesn't need content fetch)
	const _isSQLiteExample = computed(() => {
		return currentNode.value?.sType === "sqlite-example"
	})

	// Fetch content - always attempt to fetch during SSR to ensure content is pre-rendered.
	// The API will return 404 if no content exists for this path.
	// Skip fetching for SQLite example nodes since they render their own content.
	const { data: content, status: contentStatus } = useAsyncData(
		`book-content-${bookIdValue}-${contentPathValue}`,
		async () => {
			const id = bookIdValue
			const path = contentPathValue
			if (!id) return null

			// For SQLite examples, we need to check the tree first
			// If tree is loaded and this is an SQLite example, skip content fetch
			if (tree.value && path) {
				const node = findNodeByPath(tree.value, id, path)
				if (node?.sType === "sqlite-example") {
					return null
				}
			}

			try {
				const contentUrl = path
					? `/api/books/${id}/content/${path}`
					: `/api/books/${id}/content/index`
				return await $fetch<RenderedContent>(contentUrl)
			} catch {
				// Content doesn't exist for this path (e.g., container without own content)
				return null
			}
		},
		{ watch: [tree] }, // Re-run when tree loads to check node type
	)

	// Combine into a single reactive data object for backwards compatibility
	const data = computed<BookPageData>(() => ({
		bookInfo: bookInfo.value,
		tree: tree.value || [],
		content: content.value ?? null,
		currentNode: currentNode.value,
		children: children.value,
	}))

	// Status reflects content loading since tree/books are typically cached
	const status = computed(() => contentStatus.value)

	return { data, status }
}
